import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    IconButton,
    TextField,
    InputAdornment,
    CircularProgress,
    Alert,
    Chip,
    TablePagination,
} from "@mui/material";
import {
    PersonAdd as PersonAddIcon,
    Edit as EditIcon,
    // Delete as DeleteIcon,
    Search as SearchIcon,
} from "@mui/icons-material";
import Cookies from "js-cookie";
import { WB_SX, WB_UI } from "../../ui/ux/uiTokens";
import CreateSubUserModal from "./CreateSubUserModal";
import EditSubUserModal from "./EditSubUserModal";
import useNotification from "../common/Notification";
import axiosInstance from "../../api/axiosInstance";
import base_url from "../../constants";
import {
    formatRoleLabel,
    parseUserRoles,
    // primaryManagedRoleToRevoke,
    rowIsAdminOrPrivileged,
} from "./roleLabels";

const extractUsersFromAllResponse = (response) => {
    if (!response?.data) return [];
    const d = response.data;
    if (Array.isArray(d)) return d;
    if (Array.isArray(d.data)) return d.data;
    if (Array.isArray(d.users)) return d.users;
    if (d.data?.data && Array.isArray(d.data.data)) return d.data.data;
    if (d.success && Array.isArray(d.data)) return d.data;
    return [];
};

const extractAllowedUsers = (response) => {
    if (!response?.data) return [];
    const d = response.data;
    if (d.success && Array.isArray(d.data)) return d.data;
    if (Array.isArray(d.data)) return d.data;
    return [];
};

const mergeUserLists = (fromAll, fromAllowed) => {
    const map = new Map();
    fromAll.forEach((u) => {
        if (u?.id == null) return;
        map.set(Number(u.id), { ...u });
    });
    fromAllowed.forEach((u) => {
        if (u?.id == null) return;
        const id = Number(u.id);
        if (!map.has(id)) {
            map.set(id, {
                id,
                name: u.name,
                email: u.email,
                phone_number: u.phone_number,
                user_role: u.user_role ?? "",
                status: u.status ?? "ACTIVE",
            });
        }
    });
    return Array.from(map.values());
};

const UserManagement = () => {
    const [subUsers, setSubUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [openCreateModal, setOpenCreateModal] = useState(false);
    const [openEditModal, setOpenEditModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const notify = useNotification();
    const notifyRef = useRef(notify);
    notifyRef.current = notify;

    const currentUserId = useMemo(() => {
        const raw = Cookies.get("userId");
        if (raw == null) return null;
        try {
            return JSON.parse(raw);
        } catch {
            return raw;
        }
    }, []);

    const fetchSubUsers = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const [allRes, allowedRes] = await Promise.allSettled([
                axiosInstance.get(`${base_url}/whatsapp-business/users/all`, {
                    headers: { Accept: "application/json" },
                }),
                axiosInstance.get(`${base_url}/whatsapp-business/users/allowed`, {
                    headers: { Accept: "application/json" },
                }),
            ]);

            let fromAll = [];
            if (allRes.status === "fulfilled") {
                fromAll = extractUsersFromAllResponse(allRes.value);
            }

            let fromAllowed = [];
            if (allowedRes.status === "fulfilled") {
                fromAllowed = extractAllowedUsers(allowedRes.value);
            }

            let users = mergeUserLists(fromAll, fromAllowed);

            if (users.length === 0 && allRes.status === "rejected") {
                const err = allRes.reason;
                if (err?.response?.status === 404 || err?.response?.status === 405) {
                    setError("The user list could not be loaded. People you add may still show up after you create them.");
                } else {
                    const msg =
                        err?.response?.data?.message ||
                        err?.response?.data?.error ||
                        "Failed to fetch users";
                    setError(msg);
                    notifyRef.current(msg, "error");
                }
            }
            setSubUsers(users);
        } catch (err) {
            const msg = err?.response?.data?.message || err?.response?.data?.error || "Failed to fetch users";
            setError(msg);
            notifyRef.current(msg, "error");
            setSubUsers([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchSubUsers();
    }, [fetchSubUsers]);

    const handleCreateUser = () => {
        setOpenCreateModal(true);
    };

    const handleEditUser = (user) => {
        setSelectedUser(user);
        setOpenEditModal(true);
    };

    // const handleRevokeManagedUser = async (user) => {
    //     const role = primaryManagedRoleToRevoke(user.user_role);
    //     if (!role) {
    //         notify("This account has no role that you can remove here.", "warning");
    //         return;
    //     }
    //     const label = formatRoleLabel(role);
    //     if (
    //         !window.confirm(
    //             `Remove ${label} access from ${user.email || user.name || "this user"}? They will lose permissions tied to that role.`
    //         )
    //     ) {
    //         return;
    //     }
    //     try {
    //         const response = await axiosInstance.post(`${base_url}/users/revoke-role`, {
    //             user_id: user.id,
    //             role,
    //         });
    //         const ok = response.status === 200 || response.data?.success === true;
    //         if (ok) {
    //             notify(response.data?.message || "Role revoked successfully", "success");
    //             fetchSubUsers();
    //         } else {
    //             throw new Error(response.data?.message || "Failed to revoke role");
    //         }
    //     } catch (err) {
    //         const errorMessage =
    //             err?.response?.data?.message || err?.response?.data?.error || err?.message || "Failed to revoke role";
    //         notify(errorMessage, "error");
    //     }
    // };

    const handleUserCreated = () => {
        fetchSubUsers();
        setOpenCreateModal(false);
    };

    const handleUserUpdated = () => {
        fetchSubUsers();
        setOpenEditModal(false);
        setSelectedUser(null);
    };

    const filteredUsers = subUsers.filter((user) => {
        const email = user.email || "";
        const name = user.name || "";
        const searchLower = searchQuery.toLowerCase();
        return email.toLowerCase().includes(searchLower) || name.toLowerCase().includes(searchLower);
    });

    const creditsLabel = (user) => {
        const v = user.bwc ?? user.whatsapp_credits ?? user.whatsapp_credits_count;
        if (v === null || v === undefined || v === "") return "—";
        return String(v);
    };

    const renderRoleChips = (userRoleStr) => {
        const tokens = parseUserRoles(userRoleStr);
        if (tokens.length === 0) {
            return (
                <Typography variant="caption" color="text.secondary">
                    —
                </Typography>
            );
        }
        return (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {tokens.map((t) => (
                    <Chip
                        key={t}
                        label={formatRoleLabel(t)}
                        size="small"
                        variant="outlined"
                        sx={{
                            fontWeight: 600,
                            fontSize: "0.72rem",
                            height: 24,
                            borderColor: WB_UI.color.border,
                            backgroundColor: "rgba(255,255,255,0.7)",
                        }}
                    />
                ))}
            </Box>
        );
    };

    const canShowActions = (user) => {
        if (user?.id == null) return false;
        if (String(user.id) === String(currentUserId)) return false;
        if (rowIsAdminOrPrivileged(user.user_role)) return false;
        return true;
    };

    const isWbSubUser = (user) => {
        const r = String(user.user_role || "").toUpperCase();
        return r.includes("WB-USER") || r.includes("WB-AGENT");
    };

    return (
        <Box
            sx={{
                minHeight: "100%",
                bgcolor: "#F7F9FC",
                p: { xs: 1.5, sm: 2 },
                ...WB_SX.page,
            }}
        >
            <Box sx={WB_SX.headerRow}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h5" component="h1" sx={WB_SX.pageTitle}>
                        User management
                    </Typography>
                    <Typography variant="body2" sx={{ ...WB_SX.pageSubtitle, maxWidth: 720, lineHeight: 1.65 }}>
                        View people linked to your WhatsApp Business workspace and CRM tree. You can add new users or remove
                        access when someone should no longer use the system.
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<PersonAddIcon />}
                    onClick={handleCreateUser}
                    sx={WB_SX.primaryButton}
                >
                    Create user
                </Button>
            </Box>

            {error ? (
                <Alert severity="warning" sx={{ mb: 2, borderRadius: WB_UI.radius.card, ...WB_SX.card }}>
                    {error}
                </Alert>
            ) : null}

            <Box sx={{ mb: 2 }}>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Search by name or email…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: "text.secondary", fontSize: 20 }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        ...WB_SX.card,
                        bgcolor: WB_UI.color.cardBgStrong,
                        "& .MuiOutlinedInput-root": { borderRadius: WB_UI.radius.card },
                    }}
                />
            </Box>

            <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                    ...WB_SX.card,
                    bgcolor: WB_UI.color.cardBgStrong,
                    overflow: "auto",
                }}
            >
                <Table size="small" sx={{ minWidth: 720 }}>
                    <TableHead>
                        <TableRow>
                            {["ID", "Name", "Email", "Roles", "Status", "BWC / credits", "Actions"].map((h) => (
                                <TableCell key={h} sx={WB_SX.tableHeadCell}>
                                    {h}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                    <CircularProgress size={28} sx={{ color: WB_UI.color.primaryButtonBg }} />
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                                        Loading users…
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : filteredUsers.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        {searchQuery
                                            ? "No users found matching your search."
                                            : "No users found. Add someone new or try refreshing the page."}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            filteredUsers
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((user, index) => (
                                    <TableRow
                                        key={user.id || user.email || `user-${index}`}
                                        hover
                                        sx={WB_SX.tableRow}
                                    >
                                        <TableCell sx={{ fontWeight: 600, color: "text.secondary" }}>{user.id}</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>{user.name || "—"}</TableCell>
                                        <TableCell>{user.email || "—"}</TableCell>
                                        <TableCell>{renderRoleChips(user.user_role)}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={(user.status || "ACTIVE").replace(/_/g, " ")}
                                                size="small"
                                                sx={{
                                                    height: 24,
                                                    fontSize: "0.72rem",
                                                    fontWeight: 600,
                                                    textTransform: "capitalize",
                                                    ...((user.status || "ACTIVE").toUpperCase() === "ACTIVE"
                                                        ? {
                                                              bgcolor: "#ecfdf5",
                                                              color: "#047857",
                                                          }
                                                        : {
                                                              bgcolor: "#f3f4f6",
                                                              color: "#4b5563",
                                                          }),
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={creditsLabel(user)}
                                                size="small"
                                                variant="outlined"
                                                sx={{
                                                    height: 24,
                                                    fontSize: "0.72rem",
                                                    fontWeight: 600,
                                                    borderColor: WB_UI.color.border,
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {canShowActions(user) ? (
                                                <Box sx={{ display: "flex", gap: 0.25 }}>
                                                    {isWbSubUser(user) ? (
                                                        <IconButton
                                                            onClick={() => handleEditUser(user)}
                                                            size="small"
                                                            title="Update password (WhatsApp sub-user)"
                                                            sx={{
                                                                color: WB_UI.color.primaryButtonBg,
                                                                "&:hover": { bgcolor: "rgba(17,24,39,0.06)" },
                                                            }}
                                                        >
                                                            <EditIcon sx={{ fontSize: 20 }} />
                                                        </IconButton>
                                                    ) : null}
                                                    {/* Delete / remove access — temporarily disabled
                                                    {primaryManagedRoleToRevoke(user.user_role) ? (
                                                        <IconButton
                                                            onClick={() => handleRevokeManagedUser(user)}
                                                            size="small"
                                                            title="Remove user access"
                                                            sx={{
                                                                color: "#b91c1c",
                                                                "&:hover": { bgcolor: "rgba(185,28,28,0.08)" },
                                                            }}
                                                        >
                                                            <DeleteIcon sx={{ fontSize: 20 }} />
                                                        </IconButton>
                                                    ) : null}
                                                    */}
                                                </Box>
                                            ) : null}
                                        </TableCell>
                                    </TableRow>
                                ))
                        )}
                    </TableBody>
                </Table>
                {!loading && filteredUsers.length > 0 ? (
                    <TablePagination
                        component="div"
                        count={filteredUsers.length}
                        page={page}
                        onPageChange={(e, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        sx={{
                            borderTop: `1px solid ${WB_UI.color.border}`,
                            bgcolor: "rgba(255,255,255,0.5)",
                            "& .MuiTablePagination-toolbar": { minHeight: 48, px: 1 },
                        }}
                    />
                ) : null}
            </TableContainer>

            <CreateSubUserModal
                open={openCreateModal}
                onClose={() => setOpenCreateModal(false)}
                onUserCreated={handleUserCreated}
            />

            <EditSubUserModal
                open={openEditModal}
                onClose={() => {
                    setOpenEditModal(false);
                    setSelectedUser(null);
                }}
                user={selectedUser}
                onUserUpdated={handleUserUpdated}
            />
        </Box>
    );
};

export default UserManagement;
